import React, { useMemo, useState } from "react";
import style from "./style.module.scss";
import { Button, Dropdown, Icon, Tooltip } from "antd";
import {
    ExamListItem,
    ExamTimeWay,
    ExamType,
    findImportByExamIds,
    getExamTypeName
} from "../../../../../service/exam";
import DropDownCourse from "../DropDownCourse";
import ExamTime from "../../../components/ExamTime";
import { errorMessage, isTrue } from "../../../../../service/service";
import LiveButton from "../../../../../components/LiveButton";
import classNames from "classnames";
import { useCurrentTimeSync } from "utils/useCurrentDate";
import moment from "moment";
import MenuList from "./MenuList";
import ResultModal from "./ResultModal";
import useModalHandler from "utils/useModalHandler";
import { ResultMessage } from "components/ResultMessage";
import useRequestFn from "utils/useRequestFn";

interface ListItemProps extends ExamListItem {
    onEdit: () => void;
    onAdd: () => void;
    onPublish: () => void;
    onReview: () => void;
    onPreview?: () => void;
    currentTime: string;
    onSupervise: () => void;
}

export default function ListItem(props: ListItemProps) {
    const {
        type,
        name,
        courseList,
        startTime,
        endTime,
        onEdit,
        isPublish,
        onPublish,
        examId,
        examInfo,
        onReview,
        onPreview,
        onSupervise,
        timeType,
        duration,
        onAdd
    } = props;
    const serverTime = useCurrentTimeSync();
    const doing = moment(serverTime).isBetween(
        moment(startTime),
        moment(endTime)
    ); //判断当前考核是否正在进行中

    const resultModal = useModalHandler();
    const [visible, setVisible] = useState(false); //menu隐藏、展示
    const [getData, { data, loading }] = useRequestFn(findImportByExamIds);

    const handleVisibleChange = (flag: boolean) => {
        setVisible(flag);
    };

    //获取导入结果状态
    const getImportResult = async () => {
        try {
            resultModal.open();
            await getData(examId);
        } catch (e) {
            ResultMessage.error(errorMessage(e));
        }
    };

    const menu = (
        <MenuList
            {...props}
            handleMenuClick={() => setVisible(false)}
            handelResultVisible={getImportResult}
        />
    );

    const canView = useMemo(() => {
        if ([ExamType.Exam, ExamType.Thesis].includes(type)) {
            if (doing) {
                return true;
            } else {
                return props.isSetTopicTeacher === "1";
            }
        } else {
            return false;
        }
    }, [props.isSetTopicTeacher, type, doing]);

    return (
        <div
            className={classNames(
                style.root,
                style["icon_" + type],
                !isTrue(props.isInvigilator) ? style["root_" + type] : ""
            )}>
            <div className={style.wrapper}>
                <h2 className={style.title}>
                    <Tooltip title={name} placement="topLeft">
                        {name}
                    </Tooltip>
                </h2>

                <DropDownCourse data={courseList} />

                <p className={style.info}>
                    {type !== ExamType.Exam
                        ? "考试日期："
                        : timeType === ExamTimeWay.unify
                        ? "考试有效期限："
                        : "有效期："}
                    <ExamTime
                        timeType={timeType}
                        type={type}
                        startTime={startTime}
                        endTime={endTime}
                    />
                </p>
                {type === ExamType.Exam && timeType === ExamTimeWay.free && (
                    <p className={style.info}>考试期限：{duration}分钟</p>
                )}
                <p className={style.info}>
                    创建人：
                    {isTrue(props.isImport)
                        ? "系统导入"
                        : `${props.createExamTeacherName}（${props.createTeacherNo}）`}
                </p>

                <div className={style.tools}>
                    {!isTrue(isPublish) && [ExamType.Exam].includes(type) && (
                        <Dropdown
                            overlay={menu}
                            className={`${style.btnAdd} ${style.btnMargin}`}
                            placement="bottomCenter"
                            visible={visible}
                            disabled={!isTrue(props.isSetTopicTeacher)}
                            onVisibleChange={handleVisibleChange}>
                            <Button>
                                出题
                                <Icon type="down" />
                            </Button>
                        </Dropdown>
                    )}

                    {!isTrue(isPublish) && [ExamType.Thesis].includes(type) && (
                        <Button
                            className={style.btnMargin}
                            type="primary"
                            onClick={onAdd}>
                            出题
                        </Button>
                    )}

                    {isTrue(isPublish) && type === ExamType.Oral && (
                        <LiveButton
                            havePreview
                            className={`${style.btnAdd} ${style.btnMargin}`}
                            channelId={examInfo}
                            text="去考试"
                        />
                    )}

                    {isTrue(isPublish) && (
                        <Button
                            type="primary"
                            onClick={onReview}
                            className={style.btnMargin}
                            disabled={isTrue(props.isInvigilator)}>
                            判分
                        </Button>
                    )}

                    <Button
                        type="default"
                        className={style.btnMargin}
                        onClick={onPublish}
                        disabled={!isTrue(props.isSetTopicTeacher)}>
                        {isTrue(isPublish) ? "取消发布" : "发布"}
                    </Button>

                    {/* 是监考老师且考试未开始或已结束时，不显示预览按钮 */}
                    {canView && (
                        <Button
                            type="dashed"
                            className={style.btnMargin}
                            onClick={onPreview}>
                            预览
                        </Button>
                    )}

                    {!isTrue(isPublish) && [ExamType.Exam].includes(type) && (
                        <Button type="link" onClick={getImportResult}>
                            查看导入结果
                        </Button>
                    )}

                    {isTrue(isPublish) && [ExamType.Exam].includes(type) && (
                        <Button type="dashed" onClick={onSupervise}>
                            监考
                        </Button>
                    )}
                    {/* 未发布且是监考老师时不显示编辑按钮 */}
                    {!(isTrue(props.isInvigilator) && !isTrue(isPublish)) && (
                        <Button
                            type="link"
                            className={style.btnEdit}
                            onClick={onEdit}>
                            <Icon type="edit" />
                        </Button>
                    )}
                </div>

                <div className={style.icon}>{getExamTypeName(type)}</div>
            </div>

            {resultModal.render(
                <ResultModal
                    data={data || []}
                    loading={loading}
                    visible={resultModal.visible}
                    onCancel={resultModal.close}
                />
            )}
        </div>
    );
}
